/**
 * @file table.component.tsx
 * @description Компонент таблицы данных для Task Manager приложения на основе DHTMLX Grid, который обеспечивает отображение и управление табличными данными с расширенной функциональностью. Включает возможности сортировки, фильтрации, изменения размеров колонок, контекстное меню с настройками видимости колонок, прокрутку к определенным строкам, группировку данных, пагинацию и интеграцию с внешними обработчиками событий. Реализован как forwardRef компонент с императивным API для внешнего управления.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['react', '@consta/uikit/ContextMenu', '@consta/icons/IconKebab', '@consta/uikit/Switch', '@consta/uikit/Button', '@consta/icons/IconTop', '@src/modules/dhx-suite/dhx-import-fix.js', 'classnames', 'react-dom/server']
 * @related-files ['src/features/catalog/catalog.component.tsx', 'src/features/queue/queue.component.tsx', 'src/features/report/report.component.tsx']
 * @todo Переименовать интерфейс ITableHandlers для соответствия naming conventions
 */

import { useEffect, useRef, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { ContextMenu } from '@consta/uikit/ContextMenu';
import { IconKebab } from '@consta/icons/IconKebab';
import { Switch } from '@consta/uikit/Switch';
import { Button } from '@consta/uikit/Button';
import { IconTop } from '@consta/icons/IconTop';
import { getDHXGrid } from '@src/modules/dhx-suite/dhx-import-fix.js';
import '@src/modules/dhx-suite/codebase/suite.min.css';
import cn from 'classnames';
import { renderToStaticMarkup } from 'react-dom/server';
import { ITableItemDTO, TBaseObjectDto, TContextObject } from '@src/types';
import { IColumnConfig } from '../../types/pages';
import type { ICol, IRow } from '@modules/dhx-suite/codebase/types/ts-grid';

/**
 * Интерфейс для императивных методов управления таблицей
 */
export interface ITableHandlers {
    /** Прокрутка к строке с указанным ID */
    scrollTo: (id: string) => void;
    /** Получение текущей позиции прокрутки */
    getScroll: () => { x: string; y: string } | null;
    /** Прокрутка к указанным координатам */
    scroll: (x: number, y: number) => void;
    /** Выделение строки по ID */
    selectRow: (id: string) => void;
}

/**
 * Props для компонента Table с DHTMLX Grid
 */
interface ITableProps {
    /** Высота строки таблицы в пикселях */
    rowHeight?: number;
    /** Обработчик добавления новой строки */
    addRowHandler?: (item: TBaseObjectDto) => void;
    /** Подгонка размера под контейнер */
    fitToContainer?: boolean;
    /** Конфигурация колонок таблицы */
    columns: IColumnConfig[];
    /** Данные для отображения в таблице */
    items: ITableItemDTO[];
    /** Callback при прокрутке до последнего элемента */
    onScrollToLast?: () => void;
    /** Обработчик для дополнительных свойств строки */
    additionalRowHandler?: (row: TBaseObjectDto) => string;
    /** Элементы контекстного меню */
    contextMenuItemList?: TContextObject[];
    /** Текущие фильтры таблицы */
    currentFilters?: any[];
    /** Показать кнопку "Наверх" */
    isGoTopButton?: boolean;
    /** Конфигурация выделения строк */
    selection?: boolean | 'cell' | 'row' | 'complex';
    /** Режим дерева для иерархических данных */
    isTree?: boolean;
    /** Callback при обновлении данных таблицы */
    onTableDataUpdated?: () => void;
    /** Возможность добавления групп */
    isCanAddGroups?: boolean;
    /** Связывание строк */
    isLinkRows?: boolean;
    /** CSS класс для стилизации */
    className?: string;
    /** Скрыть заголовок таблицы */
    hideHeader?: boolean;
    /** Обработчик клика по строке */
    onRowClick?: (row: ITableItemDTO, column: IColumnConfig) => void;
    /** Обработчик изменений в таблице */
    onChange?: Function;
}

interface IColspanRule {
    column: string;
    colspan?: (data: any) => number;
    css?: (data: any) => string;
}

export const Table = forwardRef<ITableHandlers, ITableProps>(
    (
        {
            rowHeight = 29,
            addRowHandler = undefined,
            fitToContainer = true,
            columns,
            items,
            onScrollToLast = undefined,
            additionalRowHandler,
            contextMenuItemList = [],
            currentFilters = [],
            isGoTopButton = true,
            selection,
            isTree = false,
            onTableDataUpdated,
            isCanAddGroups = false,
            isLinkRows = false,
            className,
            hideHeader = false,
            onRowClick,
            onChange
        }: ITableProps,
        ref: React.Ref<ITableHandlers>
    ) => {
        const node = useRef<HTMLDivElement>(null);
        const [grid, setGrid] = useState<any>(null);
        const [data, setData] = useState<ITableItemDTO[]>(items);
        const dataRef = useRef<ITableItemDTO[]>(data);
        const isFetching = useRef(false);
        const contextMenuTargetRef = useRef<HTMLDivElement | null>(null);
        const sizeRatio: number = useMemo(() => Math.min(window.innerWidth, window.innerHeight) / 945 || 1, []);

        const [currentRow, setCurrentRow] = useState<any>(null);
        const [isShowGoTopButton, setIsShowGoTopButton] = useState(false);
        const [isShowContextMenu, setIsShowContextMenu] = useState(false);
        const currentRowRef = useRef(currentRow);

        const colspanRules = useMemo<IColspanRule[]>(() => {
            return columns
                .filter((column: IColumnConfig) => column.cell?.colspan)
                .map((column: IColumnConfig) => ({
                    column: column.id || '',
                    colspan: column.cell?.colspan,
                    css: column.cell?.css
                }));
        }, [columns]);

        const contextItems = useMemo<TContextObject[]>(() => {
            if (!contextMenuItemList) {
                return [];
            }

            return contextMenuItemList.map((item: any) => {
                if (item.type === 'switch') {
                    return {
                        ...item,
                        label: (
                            <Switch
                                {...item.props}
                                checked={currentRow && currentRow[item.accessor]}
                                onChange={(e) => item.clickHandler(currentRow, e.target.checked)}
                            />
                        ),
                        group: item.group
                    };
                }

                return item;
            });
        }, [contextMenuItemList, currentRow]);

        const filters = useMemo(() => {
            return currentFilters.reduce((acc: any, curr: any) => {
                acc[curr.field] = curr.value;

                return acc;
            }, {});
        }, [currentFilters]);

        const getColumns = (columnList: IColumnConfig[], withKebab: boolean) => {
            const newColumns = columnList.map((column: IColumnConfig) => {
                const newCol = { ...column, htmlEnable: true };

                if (column.width) {
                    newCol.width = column.width * sizeRatio;
                }

                if (column.render) {
                    newCol.template = (cellValue: any, row: IRow, _col: ICol) =>
                        renderToStaticMarkup(column.render!(cellValue, row));
                }

                return newCol;
            });

            const lastColumn = newColumns.at(-1);

            if (!withKebab || !newColumns || !lastColumn?.header?.[0]) {
                return newColumns;
            }

            const kebabColumn = {
                id: 'kebab',
                header: [{ text: '', rowspan: lastColumn.header[0].rowspan, css: 'border-l-none' }],
                width: 25,
                htmlEnable: true,
                mark: () => 'border-l-none kebab-button iconKebab p-0',
                template: (_text: string, row: any) =>
                    renderToStaticMarkup(
                        <div className="h-full w-full flex items-center">
                            <Button
                                className="button__item-table border-l rounded-none"
                                label="Действия"
                                size="xs"
                                iconLeft={IconKebab}
                                view="clear"
                                onlyIcon
                                onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
                                    onKebabButtonClick(e, row.id)
                                }
                            />
                        </div>
                    )
            };

            //TODO: переделать нормально
            if (contextMenuItemList?.length === 1 && contextMenuItemList[0].isOnlyIcon) {
                const button: any = contextMenuItemList[0];
                const controlButton: IColumnConfig = {
                    id: '_control',
                    header: [{ text: '', rowspan: lastColumn.header[0].rowspan, css: 'border-l-none' }],
                    width: 25,
                    htmlEnable: true,
                    onClick: (row: any, rows: any[], onChange: any) => button.clickHandler(row, rows, onChange),
                    mark: () => 'border-l-none kebab-button iconKebab p-0',
                    template: () =>
                        renderToStaticMarkup(
                            <div className="flex justify-center items-center w-full h-full dhx_grid-show-row-hover">
                                {button.icon}
                            </div>
                        )
                };

                return [...newColumns, controlButton];
            }

            return [...newColumns, kebabColumn];
        };

        const setRowStyle = (row: any) => {
            let style = row.$rowColor ? `bg-${row.$rowColor} ` : '';
            style += row.$isBold ? 'font-semibold ' : '';

            if (additionalRowHandler) {
                style += additionalRowHandler(row);
            }

            return style;
        };

        const cols = useMemo(() => getColumns(columns, !!contextMenuItemList.length), [columns]);

        const handleCellClick = (
            row: ITableItemDTO,
            column: IColumnConfig,
            event: React.MouseEvent<HTMLSpanElement, MouseEvent>
        ) => {
            if (column.id === 'kebab') {
                onKebabButtonClick(event, row.id);

                return;
            }

            if (column.id === '_control' && column.onClick) {
                column.onClick(row, dataRef.current, onChange);

                return;
            }

            if (row.isAddRow && addRowHandler) {
                addRowHandler(row);

                return;
            }

            if (onRowClick) {
                onRowClick(row, column);

                return;
            }

            if (column.cell?.onClick) {
                column.cell.onClick(row);
            }
        };

        const handleScroll = ({ y }: any) => {
            if (!node.current) {
                return;
            }

            const scrollContainer = node.current.querySelector('.dhx_grid-body');
            const containerHeight = scrollContainer?.clientHeight || 1080;
            const scrollHeight = scrollContainer?.scrollHeight || 1080;

            if (onScrollToLast && scrollHeight - containerHeight - 100 < y && !isFetching.current) {
                isFetching.current = true;
                onScrollToLast();
            }

            if (isGoTopButton) {
                setIsShowGoTopButton(y > 350);
            }

            return true;
        };

        // Инициализация грида
        useEffect(() => {
            if (grid) {
                return;
            }

            const gridProps: any = {
                rowHeight: sizeRatio * rowHeight,
                headerRowHeight: sizeRatio * rowHeight,
                tooltip: false,
                fitToContainer,
                autoWidth: true,
                type: isTree ? 'tree' : undefined,
                htmlEnable: true,
                sortable: false,
                // leftSplit: cols?.filter((column: IColumnConfig) => column.fixed).length,
                rowCss: setRowStyle,
                selection
            };

            if (!node.current) {
                return;
            }

            const GridConstructor = getDHXGrid();
            const component = new GridConstructor(node.current, gridProps);
            setGrid(component);

            component.events.on(
                'cellClick',
                (row: ITableItemDTO, column: IColumnConfig, event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                    if (column.id !== 'kebab') {
                        handleCellClick(row, column, event);
                    }
                }
            );

            component.events.on('scroll', handleScroll);

            return () => component.destructor();
        }, [cols, sizeRatio, rowHeight, isTree, fitToContainer, selection]);

        useEffect(() => {
            currentRowRef.current = currentRow;
        }, [currentRow]);

        useEffect(() => {
            setData(items);
        }, [items]);

        useEffect(() => {
            if (grid && cols) {
                grid.setColumns(cols);
            }
        }, [cols, grid]);

        useEffect(() => {
            if (!grid || !data) {
                return;
            }

            if (cols) {
                grid.setColumns(cols);
            }

            if (addRowHandler && data.length) {
                grid.removeSpan({
                    row: 0,
                    column: 0
                });
            }

            if (colspanRules) {
                data.forEach((row: ITableItemDTO) => {
                    colspanRules.forEach((colspanRule: IColspanRule) => {
                        const colspan = colspanRule.colspan ? colspanRule.colspan(row) : 1;
                        const css = colspanRule.css ? colspanRule.css(row) : '';

                        if (colspan > 1) {
                            grid.addSpan({
                                row: row.id,
                                column: colspanRule.column,
                                colspan,
                                css
                            });
                        }
                    });
                });
            }

            const selectedCell = grid.selection?.getCell();
            dataRef.current = data;
            grid.data.parse(data);

            if (selectedCell) {
                grid.selection?.setCell(selectedCell.row.id, selectedCell.column.id);
            }

            isFetching.current = false;
            onTableDataUpdated && onTableDataUpdated();
        }, [data, grid, cols, addRowHandler, colspanRules]);

        useImperativeHandle(
            ref,
            () => ({
                scrollTo(id: string): void {
                    grid?.scrollTo(id, cols[0].id);
                },
                getScroll(): { x: string; y: string } | null {
                    return grid?.getScrollState() || null;
                },
                scroll(x: number, y: number): void {
                    grid?.scroll(x, y);
                },
                selectRow(id: string): void {
                    if (!grid) {
                        return;
                    }

                    grid.selection.removeCell();
                    grid.selection.setCell(id);
                }
            }),
            []
        );

        const renderRightSide = (item: any, currentRow: ITableItemDTO) => {
            if (item.switch) {
                return (
                    <Switch
                        size="s"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            item.clickHandler(currentRow, e.target?.checked);
                            setIsShowContextMenu(false);
                        }}
                        key="Switch"
                        {...item.props}
                    />
                );
            }

            return null;
        };

        const onKebabButtonClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, rowId: string) => {
            contextMenuTargetRef.current = (e.target as HTMLElement).closest('.dhx_grid-cell');
            const clickedRow = dataRef.current.find((row: ITableItemDTO) => row.id === rowId);
            setCurrentRow(clickedRow);
            setIsShowContextMenu(!isShowContextMenu);
        };

        return (
            <div
                ref={node}
                className={cn(
                    'z-1 relative w-full h-full',
                    {
                        withKebabTable: contextMenuItemList.length,
                        'dhx_grid-link-rows': isLinkRows,
                        'hidden-header': hideHeader
                    },
                    className
                )}
            >
                {contextMenuItemList?.length > 0 && (
                    <ContextMenu
                        items={contextItems}
                        isOpen={isShowContextMenu}
                        getItemGroupId={(item: TContextObject) => item.groupId}
                        getGroupLabel={(group: any) => group.name}
                        getGroupId={(group: any) => group.key}
                        anchorRef={contextMenuTargetRef}
                        getItemLeftSide={(item) => item.icon}
                        getItemRightSide={(item) => renderRightSide(item, currentRow)}
                        onClickOutside={() => setIsShowContextMenu(false)}
                        direction="downStartRight"
                        getItemOnClick={(item) => {
                            if (!item.switch) {
                                setIsShowContextMenu(false);
                                setTimeout(() => item.clickHandler(currentRow, filters), 100);
                            }
                            return () => false;
                        }}
                        style={{ pointerEvents: 'auto' }}
                        className="absolute"
                    />
                )}

                {isShowGoTopButton && (
                    <div className="button-go-top bottom-[10.1588vmin] z-[25] absolute">
                        <div className="form-btn">
                            <Button
                                view="ghost"
                                size="m"
                                form="round"
                                style={{ width: '4.8762vmin', height: '4.8762vmin', right: '4.161vmin' }}
                                iconLeft={IconTop}
                                onClick={() => grid.scroll(0, 0)}
                                className={cn('table__button-go-top fixed', {
                                    '!bottom-[10.3619vmin]': isCanAddGroups
                                })}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
);
